import dayjs from "dayjs";
import { queryClient } from "@/api/queryClient";
import { xolaUri } from "@/api/xolaUri";
import { config } from "@/config";
import type { AccountState } from "@/hooks/useAccount";
import { useAccountStore } from "@/stores/accountStore";
import { getAccessToken, isOAuthSession, OAuthSessionStatus, useAuthStore } from "@/stores/authStore";

export const CONTRACT_VERSION = "1";

export const AuthMessage = {
    HANDSHAKE: "xola.handshake",
    HANDSHAKE_RESPONSE: "xola.handshake.response",
    AUTH_GET_TOKEN: "xola.auth.getToken",
    AUTH_GET_TOKEN_RESPONSE: "xola.auth.getToken.response",
    AUTH_GRANT: "xola.auth.grant",
    AUTH_REVOKED: "xola.auth.revoked",
} as const;

export type AuthMessageName = typeof AuthMessage[keyof typeof AuthMessage];

export type AuthScheme = "bearer" | "apiKey";

export interface Credential {
    scheme: AuthScheme;
    token: string;
    expiresAt: number | null;
}

export interface SessionData {
    sellerId: string;
    userId: string;
    apiUrl: string;
    roles: string[];
    locale: string;
    timezoneName: string;
    route: string;
    context: Record<string, unknown>;
    url: string;
    appId?: string;
    apiVersion?: string;
}

interface CreateIframeAuthHostOpts {
    getIframeWindow: () => Window | null;
    origin: string;
    getCredential: () => Credential;
    getSessionData: () => SessionData;
}

const getUtcOffsetMinutes = (timezoneName: string): number => {
    try {
        return dayjs().tz(timezoneName).utcOffset();
    } catch {
        return 0;
    }
};

export const getDefaultCredential = (): Credential => {
    if (isOAuthSession()) {
        return {
            scheme: "bearer",
            token: getAccessToken() ?? "",
            expiresAt: useAuthStore.getState().expiresAt,
        };
    }

    return {
        scheme: "apiKey",
        token: useAccountStore.getState().apiKey ?? "",
        expiresAt: null,
    };
};

export const getDefaultSessionData = (origin: string): SessionData => {
    const { seller: sellerId } = useAccountStore.getState();
    const account = queryClient.getQueryData<AccountState>(["account"]);
    const user = account?.user;
    const seller = account?.seller;

    return {
        sellerId: sellerId ?? "",
        userId: user?.id ?? "",
        apiUrl: `${xolaUri}/api`,
        roles: user?.roles ?? [],
        locale: user?.locale ?? seller?.locale ?? "en-US",
        timezoneName: seller?.timezoneName ?? "UTC",
        route: `${window.location.pathname}${window.location.search}${window.location.hash}`,
        context: {},
        url: origin,
        apiVersion: config.xola.apiVersion,
    };
};

export const createIframeAuthHost = (opts: CreateIframeAuthHostOpts): { destroy: () => void } => {
    const { getIframeWindow, origin, getCredential, getSessionData } = opts;

    const post = (event: AuthMessageName, data: Record<string, unknown>) => {
        const win = getIframeWindow();
        if (!win) return;
        win.postMessage(JSON.stringify({ event, data }), origin);
    };

    const onMessage = (event: MessageEvent) => {
        const iframeWindow = getIframeWindow();
        if (!iframeWindow || event.source !== iframeWindow) return;
        if (event.origin !== origin) return;

        if (typeof event.data !== "string") return;

        let parsed: { event?: string; data?: unknown };
        try {
            parsed = JSON.parse(event.data);
        } catch {
            return;
        }

        if (!parsed.event) return;

        if (parsed.event === AuthMessage.HANDSHAKE) {
            const sessionData = getSessionData();
            if (!sessionData.userId) return;
            const credential = getCredential();
            post(AuthMessage.HANDSHAKE_RESPONSE, {
                contractVersion: CONTRACT_VERSION,
                timezoneOffset: getUtcOffsetMinutes(sessionData.timezoneName),
                ...credential,
                ...sessionData,
            });
            return;
        }

        if (parsed.event === AuthMessage.AUTH_GET_TOKEN) {
            const credential = getCredential();
            post(AuthMessage.AUTH_GET_TOKEN_RESPONSE, { ...credential });
            return;
        }
    };

    window.addEventListener("message", onMessage);

    const unsubscribe = useAuthStore.subscribe((state, previousState) => {
        if (
            previousState.status === OAuthSessionStatus.AUTHENTICATED &&
            state.status === OAuthSessionStatus.AUTHENTICATED
        ) {
            const credential = getCredential();
            post(AuthMessage.AUTH_GRANT, { contractVersion: CONTRACT_VERSION, ...credential });
        }

        if (
            state.status === OAuthSessionStatus.SIGNED_OUT &&
            previousState.status !== OAuthSessionStatus.SIGNED_OUT &&
            isOAuthSession()
        ) {
            post(AuthMessage.AUTH_REVOKED, { reason: "session_ended" });
        }
    });

    return {
        destroy() {
            window.removeEventListener("message", onMessage);
            unsubscribe();
        },
    };
};
